import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import type { ErrorResponse } from '@/types/apiTypes';

export const setBEFieldError = <T extends FieldValues>(error: ErrorResponse<unknown>, setError: UseFormSetError<T>) => {
  if (error) {
    try {
      const parsedError = error as ErrorResponse<T>;

      if (parsedError.errors) {
        const processErrors = (errors: Record<string, unknown>, prefix = '') => {
          Object.entries(errors).forEach(([key, value]) => {
            if (key !== 'details') {
              const fieldKey = prefix ? `${prefix}.${key}` : key;

              if (typeof value === 'string' || Array.isArray(value)) {
                const errorMessage = Array.isArray(value) ? value.join(', ') : value;
                if (errorMessage) {
                  setError(fieldKey as Path<T>, {
                    type: 'server',
                    message: errorMessage,
                  });
                }
              } else if (value && typeof value === 'object') {
                processErrors(value as Record<string, unknown>, fieldKey);
              }
            }
          });
        };

        processErrors(parsedError.errors);
      }
    } catch (e) {
      console.error('Error parsing error:', e);
    }
  } else {
    console.error('Error is missing:', error);
  }
};

export function flattenErrors<T extends FieldValues>(obj: ErrorResponse<T>, prefix = ''): Record<string, string> {
  const flattenedErrors: Record<string, string> = {};

  Object.entries(obj).forEach(([key, value]) => {
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (Array.isArray(value)) {
      flattenedErrors[newKey] = value.join(', ');
    } else if (value && typeof value === 'object') {
      Object.assign(flattenedErrors, flattenErrors(value as unknown as ErrorResponse<T>, newKey));
    } else if (typeof value === 'string') {
      flattenedErrors[newKey] = value;
    } else {
      console.warn(`Unexpected value type at ${newKey}:`, value);
    }
  });

  return flattenedErrors;
}